import { useState, useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import './App.css'
import themeConfig from '../theme.config'
import { MarkupProvider } from './providers/Markup'
import { JourneyGateProvider } from './providers/JourneyGate'
import Page from './components/Page'
import LockedPage from './components/LockedPage'
import LockedContentBanner from './components/sections/LockedContentBanner'
import Loader from './components/helpers/Loader'
import { scrollToHashTarget } from './helpers/hashScroll'

async function getComponents() {
  try {
    const response = await fetch('/data.json')
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

/**
 * The D365 Marketing FormLoader bundle URL comes from appsettings
 * (D365Forms:FormLoaderScriptUrl) via window.pageData, not a hardcoded constant.
 * Apply it to the third-party loader config once at startup — before any form
 * component mounts and calls runThirdParty() — so the loader script loads from
 * server config rather than theme.config.js.
 */
function applyFormLoaderScriptUrl(data) {
  const url = data?.result?.formLoaderScriptUrl
  const d365Script = themeConfig.thirdParty?.d365Forms?.script
  if (url && d365Script) {
    d365Script.src = url
  }
}

/**
 * Public paths under /member that do not require authentication.
 * All other /member/* paths redirect to /member/login if the visitor is not signed in.
 */
const MEMBER_PUBLIC_PATHS = [
  '/member/login',
  '/member/forgot-password',
  '/member/reset-password',
  '/member/registration',
]

/**
 * Guards all /member/* pages. Redirects unauthenticated visitors to /member/login
 * with the current path as returnUrl so they land back after signing in, and
 * redirects authenticated members without portal-section access off the
 * privileged portal pages (see redirectDeniedPortalPage).
 * Skips paths listed in MEMBER_PUBLIC_PATHS.
 */
async function checkMemberAreaAuth() {
  const path = window.location.pathname.replace(/\/$/, '') || '/'

  // Path-segment boundary check, NOT a raw string prefix: `/membership`,
  // `/membership/benefits` etc. are public content pages that must NOT be
  // treated as the member area. `'/membership'.startsWith('/member')` is true,
  // so a plain prefix check would bounce those public pages to /member/login.
  if (path !== '/member' && !path.startsWith('/member/')) return
  if (MEMBER_PUBLIC_PATHS.some((p) => path.startsWith(p))) return

  try {
    const res = await fetch('/umbraco/surface/membercontext/current', { credentials: 'same-origin' })
    if (!res.ok) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = `/member/login?returnUrl=${returnUrl}`
      return
    }
    await redirectDeniedPortalPage(res, path)
  } catch {
    // Network error — leave the page as-is; server-side auth will handle it on the next request
  }
}

/**
 * Members outside the portal-role allowlist must not sit on the privileged
 * portal pages (Membership Details / Organisation Details / Organisation
 * Information): the server has already stripped their sections from the payload
 * (MemberPortalSectionGateService), so there is nothing for them to see — send
 * them to their profile instead. Runs at the app level (not inside a member
 * component) so it fires whatever the page is built from, including pages whose
 * only member block was stripped server-side and therefore never mounts.
 * All URLs come from CrmMember appsettings via the member-context payload —
 * nothing is hardcoded here. A payload without the flag/URLs (older API) simply
 * never matches, leaving the page as-is.
 */
async function redirectDeniedPortalPage(res, path) {
  const data = await res.json().catch(() => null)
  if (!data || data.canAccessMemberPortalSections === true) return

  const urls = data.memberPortalUrls
  if (!urls?.myProfile) return

  const normalise = (url) => (typeof url === 'string' ? url.replace(/\/+$/, '').toLowerCase() : '')
  const privilegedPaths = [urls.membershipDetails, urls.organisationDetails, urls.organisationInformation]
    .map(normalise)
    .filter(Boolean)

  if (privilegedPaths.includes(path.toLowerCase())) {
    window.location.replace(urls.myProfile)
  }
}

/**
 * The only pages where the journey re-entry check should fire.
 * These are the renewal-journey steps where a member's D365 status determines
 * which page they belong on. All other member pages (my-details, etc.)
 * are accessible regardless of payment status and must not trigger a redirect.
 *
 * /member (profile) is intentionally NOT included: the Member Profile page must
 * stay reachable via "My Profile" and breadcrumbs regardless of pending
 * renewal / approval / payment status. The profile already surfaces the renewal
 * call-to-action (RenewButton / RenewalBadge) so a pending member can act from
 * there — it must not auto-redirect them away. (NDS-180 had added /member here,
 * which removed every navigation path to the profile; reverted per follow-up.)
 *
 * NOTE: /member/receipt is intentionally excluded. After payment succeeds the
 * frontend navigates to receipt, but D365 status propagates asynchronously —
 * running the check there would read stale "pending payment" status and
 * immediately bounce the member back to the invoice page.
 */
const JOURNEY_CHECK_PATHS = [
  '/member/renewals',
  '/member/invoice',
  '/member/pending-approval',
]

/**
 * The payment page is a deliberate special case, NOT part of JOURNEY_CHECK_PATHS.
 * A member who has legitimately reached payment still reads as "pending payment" in
 * D365, and the status-only journey mapping would send that to the invoice page and
 * bounce them back out of payment. So the payment page instead passes its own path to
 * the status endpoint and obeys the page-aware guard decision (the same rules the
 * server-side MembersController applies): a member with nothing to pay
 * (Current / Current - Pending Renewal) → their profile, one awaiting approval →
 * the pending-approval page, while a legitimate "Current - Pending Payment" payer is
 * kept on the form.
 */
const PAYMENT_PATH = '/member/payment'

/**
 * Calls /umbraco/surface/journey/status for re-entry routing.
 * Fires on the journey steps in JOURNEY_CHECK_PATHS (any status → its canonical
 * page, status-only mapping) and on the payment page (PAYMENT_PATH), which passes
 * currentPath so the endpoint returns the page-aware guard decision. Never fires on
 * the profile, receipt, or other member pages.
 */
function checkMemberJourneyStatus() {
  const path = window.location.pathname.replace(/\/$/, '') || '/'

  const isJourneyPath = JOURNEY_CHECK_PATHS.includes(path)
  const isPaymentPath = path === PAYMENT_PATH
  if (!isJourneyPath && !isPaymentPath) return

  // Only the payment page passes currentPath (page-aware guard decision); the other journey
  // pages keep using the status-only mapping so their polling behaviour is unchanged.
  const statusUrl = isPaymentPath
    ? `/umbraco/surface/journey/status?currentPath=${encodeURIComponent(path)}`
    : '/umbraco/surface/journey/status'

  fetch(statusUrl, { credentials: 'same-origin' })
    .then((res) => {
      if (!res.ok) return
      return res.json()
    })
    .then((data) => {
      if (!data?.redirectUrl) return
      const target = data.redirectUrl.replace(/\/$/, '')
      if (!target || target === path) return
      window.location.href = target
    })
    .catch(() => {
      // Non-fatal — if the check fails the member stays on the current page
    })
}

/**
 * Checks whether the current visitor can see locked content.
 *
 * First asks `/api/content/member-status` — if the visitor is already
 * authenticated as an Umbraco member (and not in a role/category restricted
 * from locked content) the banner is skipped entirely and we avoid an
 * unnecessary D365 round-trip. Unauthenticated visitors AND restricted members
 * (General Registrants, non-Platinum Industry Supporters) fall through
 * to the authoritative `/api/content/access` check, which denies them.
 */
async function requestContentAccess(pathnameWithQuery, setLockAccess) {
  const encoded = encodeURIComponent(pathnameWithQuery)
  const query = `url=${encoded}&returnUrl=${encoded}`

  try {
    const statusRes = await fetch('/api/content/member-status', { credentials: 'include' })
    if (statusRes.ok) {
      const { isLoggedIn, restrictedFromLockedContent } = await statusRes.json()
      if (isLoggedIn && !restrictedFromLockedContent) {
        setLockAccess({ status: 'allowed' })
        return
      }
    }

    const response = await fetch(`/api/content/access?${query}`, { credentials: 'include' })
    if (response.status === 404) {
      setLockAccess({ status: 'allowed' })
      return
    }
    if (!response.ok) {
      setLockAccess({ status: 'blocked', loginUrl: null })
      return
    }
    const data = await response.json()
    if (data.canAccess) {
      setLockAccess({ status: 'allowed' })
      return
    }
    setLockAccess({ status: 'blocked', loginUrl: data.loginUrl ?? null })
  } catch {
    setLockAccess({ status: 'blocked', loginUrl: null })
  }
}

function App() {
  const [compData, setCompData] = useState()
  const [lockAccess, setLockAccess] = useState({ status: 'idle' })

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.pageData) {
      applyFormLoaderScriptUrl(window.pageData)
      setCompData(window.pageData)
    } else {
      const comps = getComponents()
      comps.then((response) => {
        if (!response.error) {
          applyFormLoaderScriptUrl(response)
          setCompData(response)
        }
      })
    }
    checkMemberAreaAuth()
    checkMemberJourneyStatus()
  }, [])

  useEffect(() => {
    // A bfcache restore (browser Back/forward) does not re-run the mount effect above, so the auth
    // and journey guards would be skipped on a Back navigation back to a guarded page. Re-run them
    // when the page is shown from the bfcache. Journey pages also send Cache-Control: no-store, so
    // in most browsers Back is a fresh server request (event.persisted === false) and this is a
    // cross-browser safety net for any browser that restores the page regardless.
    const handlePageShow = (event) => {
      if (!event.persisted) return
      checkMemberAreaAuth()
      checkMemberJourneyStatus()
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && compData?.result) {
      console.log('📄 Page Data', compData)
    }
  }, [compData])

  useEffect(() => {
    if (!compData?.result || compData.error) {
      return
    }
    const { result } = compData
    if (!result.isLocked) {
      setLockAccess({ status: 'allowed' })
      return
    }
    // Server-side gate (LockedContentGateService): when the payload carries a verdict,
    // the server has already checked access and — on denial — truncated `components`
    // to the teaser slice before serialising. Trust it and skip the client round trip.
    // Payloads without the flag (e.g. the /data.json dev fallback) use the legacy
    // client-side check below.
    if (typeof result.lockedContentAccessDenied === 'boolean') {
      setLockAccess({ status: result.lockedContentAccessDenied ? 'blocked' : 'allowed' })
      return
    }
    setLockAccess({ status: 'pending' })
    const path = `${window.location.pathname}${window.location.search}`
    requestContentAccess(path, setLockAccess)
  }, [compData])

  const result = compData?.result
  const pagePayloadOk = !!(result && !compData.error)
  const isLockedPage = pagePayloadOk && !!result.isLocked
  const showFullPage = pagePayloadOk && (!isLockedPage || lockAccess.status === 'allowed')
  const showLockedBanner = isLockedPage && lockAccess.status === 'blocked'
  const showLockCheckLoader =
    isLockedPage && lockAccess.status !== 'allowed' && lockAccess.status !== 'blocked'

  useEffect(() => {
    // Deep links (`/page#section`) cannot rely on the browser's native fragment scroll:
    // it runs before React has rendered anything into the empty `#root`, so the anchor
    // does not exist yet. Retry once the payload is in and sections are mounting.
    // Keyed on the payload rather than the mount so it also covers the locked-content
    // path, where the real content only appears after the access check resolves.
    if (!pagePayloadOk) {
      return undefined
    }
    let cancelWait = scrollToHashTarget()
    const handleHashChange = () => {
      cancelWait()
      cancelWait = scrollToHashTarget()
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => {
      cancelWait()
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [pagePayloadOk])

  const lockedBannerElement = showLockedBanner ? (
    <LockedContentBanner
      joinUrl={result.lockedContentBannerSettings?.joinPageUrl || '/membership'}
      forgotPasswordUrl={result.lockedContentBannerSettings?.forgotPasswordUrl}
      signInTitle={result.lockedContentBannerSettings?.signInTitle}
      registrationPromoText={result.lockedContentBannerSettings?.registrationPromoText}
      loginButtonText={result.lockedContentBannerSettings?.loginButtonText}
      forgotPasswordText={result.lockedContentBannerSettings?.forgotPasswordText}
      joinButtonLabel={result.lockedContentBannerSettings?.joinButtonLabel}
    />
  ) : null

  return (
    <>
      {compData?.result && !compData.error && (
        <HelmetProvider>
          <Helmet>
            {compData.result.pageDescription && compData.result.pageDescription.length > 0 && (
              <meta name="description" content={compData.result.pageDescription} />
            )}
            {compData.result.pageKeywords && compData.result.pageKeywords.length > 0 && (
              <meta name="keywords" content={compData.result.pageKeywords} />
            )}
            {compData.result.canonicalUrl && compData.result.canonicalUrl.length > 0 && (
              <link rel="canonical" href={compData.result.canonicalUrl} />
            )}
            {compData.result.ogTitle && compData.result.ogTitle.length > 0 && (
              <meta content={compData.result.ogTitle} property="og:title" />
            )}
            {compData.result.ogDescription && compData.result.ogDescription.length > 0 && (
              <meta content={compData.result.ogDescription} property="og:description" />
            )}
            {compData.result.ogKeywords && compData.result.ogKeywords.length > 0 && (
              <meta content={compData.result.ogKeywords} property="og:keywords" />
            )}
            {compData.result.ogType && compData.result.ogType.length > 0 && (
              <meta content={compData.result.ogType} property="og:type" />
            )}
            {compData.result.ogImage && compData.result.ogImage.length > 0 && (
              <meta content={compData.result.ogImage} property="og:image" />
            )}
          </Helmet>
        </HelmetProvider>
      )}
      {pagePayloadOk && (
        <MarkupProvider>
          {showLockCheckLoader && (
            <div className="flex justify-center py-24" role="status" aria-live="polite">
              <Loader />
            </div>
          )}
          {showFullPage && (
            <JourneyGateProvider components={compData.result.components}>
              <Page
                res={compData.result}
                nav={compData.result.navigation}
                breadcrumbs={compData.result.breadcrumbs}
                footer={compData.result.footer}
                content={compData.result.components}
              />
            </JourneyGateProvider>
          )}
          {showLockedBanner && (
            <LockedPage
              res={compData.result}
              nav={compData.result.navigation}
              breadcrumbs={compData.result.breadcrumbs}
              footer={compData.result.footer}
              content={compData.result.components}
              lockedBanner={lockedBannerElement}
            />
          )}
        </MarkupProvider>
      )}
    </>
  )
}

export default App
